"use client";

import { useMemo } from "react";

import { SCHEDULE_UPCOMING_SOON_MINUTES } from "../_logic/constants";
import type { ScheduleItemRow } from "../_logic/types/db-types";
import { buildTimedItems, computeLiveStatus } from "../_logic/utils/schedule-utils";
import { useClientNow } from "./useNow";

/** Which items to mark as in progress or about to start. */
export interface ScheduleLiveMarkers {
  /** Ids of items in progress right now. */
  liveItemIds: Set<number>;
  /** Minutes until start, for items starting within `SCHEDULE_UPCOMING_SOON_MINUTES`. */
  upcomingMinutesById: Map<number, number>;
}

/**
 * Works out which of `items` are in progress and which start soon, re-checked on the schedule
 * clock tick. Floating-time schedules (no zone) and the server render get no markers.
 *
 * @param items - The items to check, usually one day's.
 * @param timeZone - The page's IANA timezone, or null for floating time.
 * @returns The live and upcoming markers.
 */
export function useScheduleLiveMarkers(items: ScheduleItemRow[], timeZone: string | null): ScheduleLiveMarkers {
  const now = useClientNow();
  // Resolve instants once per item change; the per-tick memo below only compares numbers.
  const timedItems = useMemo(() => (timeZone ? buildTimedItems(items, timeZone) : []), [items, timeZone]);

  return useMemo(() => {
    const liveItemIds = new Set<number>();
    const upcomingMinutesById = new Map<number, number>();
    if (now == null) return { liveItemIds, upcomingMinutesById };
    const status = computeLiveStatus(timedItems, now, SCHEDULE_UPCOMING_SOON_MINUTES);
    if (status.kind === "live") {
      for (const entry of status.items) liveItemIds.add(entry.item.id);
      for (const upcoming of status.upcoming) upcomingMinutesById.set(upcoming.item.item.id, upcoming.startsInMinutes);
    } else if (status.kind === "next" && status.startsInMinutes <= SCHEDULE_UPCOMING_SOON_MINUTES) {
      // Nothing live, but the next item is close: the live state's `upcoming` list only fills
      // while something is live.
      upcomingMinutesById.set(status.item.item.id, status.startsInMinutes);
    }
    return { liveItemIds, upcomingMinutesById };
  }, [timedItems, now]);
}
